import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Callout,
  EmptyState,
  Icon,
  Progress,
  Rule,
  SectionHeader,
  Spinner,
  Stamp,
  Stat,
  Tag,
  Textarea,
} from "../ui";
import { LABELS_16, MOCK_EXTRACTIONS } from "../data/mock";
import { useBuildMonographPt } from "../hooks/useBuildMonographPt";
import { useExtractions, usePatchExtraction } from "../hooks/useExtractions";
import { useJobPolling } from "../hooks/useJobPolling";
import { useTranslateProject } from "../hooks/useTranslateProject";
import type { Extraction, Project } from "../types";

export interface Step7TranslationProps {
  project: Project;
  mock: boolean;
  onAdvance: (n: number) => void;
}

function warningNotes(notes: string | null | undefined): string | null {
  if (!notes) return null;
  const lines = notes
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  return lines.find((line) => line.includes("[translation-validation]")) ?? null;
}

function ptText(extraction: Extraction): string {
  return extraction.text_edited_pt || extraction.text_translated_pt || "";
}

function enText(extraction: Extraction): string {
  return extraction.text_edited_en || extraction.text_verbatim_en;
}

export function Step7Translation({ project, mock, onAdvance }: Step7TranslationProps) {
  const qc = useQueryClient();
  const extractionsQuery = useExtractions(project.id, { status: "approved" }, !mock);
  const patchExtraction = usePatchExtraction(project.id);
  const translate = useTranslateProject(project.id);
  const buildPt = useBuildMonographPt(project.id);
  const [translateJobId, setTranslateJobId] = useState<string | null>(null);
  const [buildJobId, setBuildJobId] = useState<string | null>(null);
  const translateJob = useJobPolling(translateJobId);
  const buildJob = useJobPolling(buildJobId);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [expandedEn, setExpandedEn] = useState<Set<string>>(new Set());

  const approved = mock
    ? MOCK_EXTRACTIONS.filter((e) => e.status === "approved")
    : (extractionsQuery.data ?? []);
  const translatedCount = approved.filter((e) => Boolean(ptText(e).trim())).length;
  const warningCount = approved.filter((e) => warningNotes(e.review_notes)).length;
  const allHavePt = approved.length > 0 && translatedCount === approved.length;
  const translationRunning =
    translate.isPending ||
    (translateJob.data ? !translateJob.isTerminal : project.status === "translating");
  const buildRunning =
    buildPt.isPending ||
    (buildJob.data ? !buildJob.isTerminal : project.status === "building");
  const actionsDisabled = translationRunning || buildRunning || patchExtraction.isPending;
  const progressValue = translateJob.data?.progress ?? (translationRunning ? 15 : allHavePt ? 100 : 0);
  const hasError = !mock && extractionsQuery.isError;
  const isLoading = !mock && extractionsQuery.isPending;

  const sortedApproved = useMemo(
    () =>
      [...approved].sort((a, b) => {
        const aCitation = a.citation_number ?? 10_000;
        const bCitation = b.citation_number ?? 10_000;
        return a.label.localeCompare(b.label) || aCitation - bCitation || a.id.localeCompare(b.id);
      }),
    [approved],
  );

  useEffect(() => {
    if (translateJob.data?.status === "success") {
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["extractions", project.id] });
      qc.invalidateQueries({ queryKey: ["extractions-summary", project.id] });
    }
  }, [project.id, qc, translateJob.data?.status]);

  useEffect(() => {
    if (buildJob.data?.status === "success") {
      qc.invalidateQueries({ queryKey: ["project", project.id] });
      qc.invalidateQueries({ queryKey: ["monograph", project.id] });
      qc.invalidateQueries({ queryKey: ["extractions", project.id] });
      onAdvance(8);
    }
  }, [buildJob.data?.status, onAdvance, project.id, qc]);

  const startTranslation = async () => {
    const response = await translate.mutateAsync();
    setTranslateJobId(response.job_id);
  };

  const startBuildPt = async () => {
    const response = await buildPt.mutateAsync();
    setBuildJobId(response.job_id);
  };

  const startEdit = (extraction: Extraction) => {
    setEditingId(extraction.id);
    setEditText(ptText(extraction));
  };

  const saveEdit = async () => {
    if (!editingId) return;
    const normalized = editText.trim();
    await patchExtraction.mutateAsync({
      id: editingId,
      body: { text_edited_pt: normalized ? normalized : null },
    });
    setEditingId(null);
  };

  const toggleEn = (id: string) => {
    setExpandedEn((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="anim-fade">
      <SectionHeader
        number={7}
        eyebrow="Etapa 07 · Tradução"
        title="Revisão PT-BR"
        lede="Os trechos aprovados em inglês são traduzidos com chamada forçada ao modelo e revisados antes da compilação final em PT-BR."
        action={
          <Button
            variant="ink"
            iconRight={Icon.ArrowRight}
            disabled={!allHavePt || actionsDisabled}
            onClick={startBuildPt}
          >
            Compilar monografia PT-BR
          </Button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-y-2 border-[var(--ink)] mb-8">
        <div className="px-6 py-5 border-r border-[var(--rule)]">
          <Stat value={approved.length} label="Aprovados EN" size="md" />
        </div>
        <div className="px-6 py-5 lg:border-r border-[var(--rule)]">
          <Stat value={translatedCount} label="Com PT-BR" size="md" />
        </div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-r border-[var(--rule)]">
          <Stat value={warningCount} label="Alertas" size="md" />
        </div>
        <div className="px-6 py-5 border-t lg:border-t-0 border-[var(--rule)]">
          <Stat value={project.current_step} label="Etapa atual" size="md" />
        </div>
      </div>

      {isLoading && (
        <div className="caption flex items-center gap-3 mb-8">
          <Spinner /> Carregando traduções...
        </div>
      )}
      {hasError && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Traduções indisponíveis">
          Não foi possível carregar os trechos aprovados. Recarregue a página ou tente novamente.
        </Callout>
      )}

      <div className="border-y border-[var(--ink)] py-5 mb-8 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="eyebrow">Tradução automática</div>
          <p className="caption mt-1">
            {allHavePt
              ? "Todos os trechos aprovados têm texto PT-BR para revisão."
              : `${Math.max(approved.length - translatedCount, 0)} trechos ainda sem PT-BR.`}
          </p>
          {translateJob.data && (
            <p className="mono text-[11px] text-[var(--ink-3)] mt-1">
              Job {translateJob.data.status} · {translateJob.data.progress}%
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ink"
            size="sm"
            icon={translationRunning ? Icon.Loader : Icon.Languages}
            onClick={startTranslation}
            disabled={approved.length === 0 || translationRunning || buildRunning}
          >
            Iniciar tradução
          </Button>
          <Button
            variant="line"
            size="sm"
            icon={buildRunning ? Icon.Loader : Icon.FileText}
            onClick={startBuildPt}
            disabled={!allHavePt || actionsDisabled}
          >
            Compilar PT-BR
          </Button>
        </div>
      </div>

      {(translationRunning || translateJob.data) && (
        <div className="border border-[var(--ink)] p-7 bg-[var(--paper-2)] mb-8">
          <div className="flex items-baseline justify-between mb-5 gap-4">
            <div className="flex items-baseline gap-3">
              {translationRunning ? <Spinner /> : <Icon.CheckCircle size={17} />}
              <div className="display-5 leading-none">
                {translationRunning ? "Traduzindo" : "Tradução concluída"}
              </div>
            </div>
            <Stamp tone={translateJob.isFailed ? "strike" : "ink"}>
              {translateJob.data?.status ?? project.status}
            </Stamp>
          </div>
          <Progress
            value={progressValue}
            label="Progresso"
            sub={translateJob.data?.progress_message ?? "Batches de trechos aprovados"}
          />
        </div>
      )}

      {translateJob.isFailed && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Falha na tradução">
          {translateJob.data?.error_message || "Revise os logs do job e tente novamente."}
        </Callout>
      )}
      {buildJob.isFailed && (
        <Callout tone="strike" icon={Icon.AlertCircle} title="Falha ao compilar PT-BR">
          {buildJob.data?.error_message || "Verifique se todos os trechos aprovados têm PT-BR e citações."}
        </Callout>
      )}

      <Rule className="mb-6">§ 7.1 · Trechos aprovados</Rule>

      {!isLoading && !hasError && sortedApproved.length === 0 ? (
        <EmptyState
          icon={Icon.Languages}
          title="Nenhum trecho aprovado"
          description="A tradução PT-BR aparece aqui depois que a revisão EN aprova pelo menos um trecho."
        />
      ) : (
        <div className="anim-stagger">
          {sortedApproved.map((extraction, index) => {
            const label = LABELS_16.find((item) => item.key === extraction.label);
            const isEditing = editingId === extraction.id;
            const translated = ptText(extraction);
            const warning = warningNotes(extraction.review_notes);
            const showEn = expandedEn.has(extraction.id);
            return (
              <article key={extraction.id} className="py-8 border-b border-[var(--rule-2)]">
                <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
                  <div className="flex items-baseline gap-3">
                    <span className="step-num text-[24px] tabular text-[var(--ink-4)]">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="display-5">{label?.title_pt ?? extraction.label}</h3>
                    <span className="caption">· {label?.title_en ?? extraction.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {extraction.citation_number && (
                      <Tag tone="rule">Citação ({extraction.citation_number})</Tag>
                    )}
                    {warning && <Tag tone="strike">Revisar</Tag>}
                    {extraction.text_edited_pt && <Tag tone="ok">Editado</Tag>}
                  </div>
                </div>

                {warning && (
                  <div className="mb-5">
                    <Callout tone="rule" icon={Icon.AlertTriangle} title="Alerta de validação">
                      {warning.replace("[translation-validation]", "").trim()}
                    </Callout>
                  </div>
                )}

                <div className="grid grid-cols-12 gap-7">
                  <div className="col-span-12">
                    <div className="flex items-center justify-between mb-2">
                      <span className="caption">PT-BR · texto principal</span>
                      {!isEditing && (
                        <Button
                          variant="ghost"
                          size="xs"
                          icon={Icon.Edit}
                          onClick={() => startEdit(extraction)}
                          disabled={actionsDisabled}
                        >
                          Editar
                        </Button>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-3">
                        <Textarea
                          value={editText}
                          onChange={(event) => setEditText(event.target.value)}
                          rows={6}
                        />
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingId(null)}
                            disabled={patchExtraction.isPending}
                          >
                            Cancelar
                          </Button>
                          <Button
                            variant="ink"
                            size="sm"
                            icon={patchExtraction.isPending ? Icon.Loader : Icon.Check}
                            onClick={saveEdit}
                            disabled={patchExtraction.isPending}
                          >
                            Salvar PT-BR
                          </Button>
                        </div>
                      </div>
                    ) : translated ? (
                      <p className="serif text-[16px] leading-[1.65] text-[var(--ink)]">
                        {translated}
                      </p>
                    ) : (
                      <p className="caption italic">Sem tradução PT-BR.</p>
                    )}
                  </div>

                  <div className="col-span-12">
                    <Button
                      variant="quiet"
                      size="sm"
                      icon={showEn ? Icon.EyeOff : Icon.Eye}
                      onClick={() => toggleEn(extraction.id)}
                    >
                      {showEn ? "Ocultar original EN" : "Ver original EN"}
                    </Button>
                    {showEn && (
                      <div className="mt-4 pl-5 border-l border-[var(--rule)]">
                        <div className="caption mb-2">EN · original aprovado</div>
                        <p className="serif text-[15px] leading-[1.65] text-[var(--ink-2)]">
                          {enText(extraction)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
